import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Stack,
  Title,
  Card,
  Group,
  Button,
  Text,
  Divider,
  Badge,
  Alert,
  Code,
  Modal,
  Table,
  Loader,
  Center,
  Anchor,
  Grid,
  SimpleGrid,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconDownload,
  IconCopy,
  IconArrowLeft,
  IconAlertCircle,
  IconEdit,
  IconHistory,
  IconBuilding,
  IconTruck,
  IconUser,
  IconRoute,
  IconCalendar,
  IconBox,
  IconWeight,
  IconHash,
  IconClipboardList,
  IconQrcode,
  IconReceipt,
  IconFileText,
  IconShieldCheck,
  IconInfoCircle,
  IconNotes,
  IconPlayerPlay,
  IconFlag,
  IconBan,
  IconCheck,
} from '@tabler/icons-react';
import {
  useDeCA,
  useVersionDeCA,
  useVersionesDeCA,
  useUpdateEnvioEstado,
  getErrorMessage,
} from '../../api/hooks';
import type { DeCaEnriched, DeCaVersion, EnvioEstado, EnvioTipoServicio } from '../../api/types';
import { UNIDAD_CANTIDAD_LABEL } from '../../api/types';
import { ConfirmDialog } from '../../components/ConfirmDialog';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

// Estados de envío en los que está permitida la modificación
// (Apartado Quinto de la Resolución 5 junio 2026).
const ESTADOS_ENVIO_MODIFICABLES = new Set(['EMITIDO', 'EN_CURSO']);

const TIPO_SERVICIO_LABEL: Record<EnvioTipoServicio, string> = {
  MERCANCIA: 'Mercancía',
  VIAJERO_DISCRECIONAL: 'Viajeros — Discrecional',
  VIAJERO_TURISTICO: 'Viajeros — Turístico',
  COLABORACION: 'Colaboración entre transportistas',
};

/**
 * Vista de detalle de un DeCA. Muestra TODOS los datos del documento:
 * cabecera, envío, partes, servicios (origen/destino + mercancía),
 * URL pública/QR, histórico de versiones y metadatos de auditoría.
 *
 * Layout uniforme: una columna con `Stack`, contenido agrupado en
 * `Card`s con titulo + Descripcion. Cada campo se pinta con
 * `FieldRow` (label a la izquierda, valor a la derecha con tipografía
 * consistente — monospace para IDs/matrículas, normal para el resto).
 *
 * El objetivo es que un operario que abre esta pagina tenga a la vista
 * toda la informacion que aparece en el PDF oficial, sin tener que
 * descargar el documento.
 */
export function DeCADetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: deca, isLoading, error } = useDeCA(id);
  const { data: versiones, isLoading: isLoadingVersiones } = useVersionesDeCA(id);
  const cambiarEstado = useUpdateEnvioEstado();

  const [versionSeleccionada, setVersionSeleccionada] = useState<number | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  if (isLoading) {
    return (
      <Center mih={200}>
        <Loader />
      </Center>
    );
  }

  if (error || !deca) {
    return (
      <Stack gap="md">
        <Group px="md" pt="md">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={18} />}
            onClick={() => navigate('/deca')}
          >
            Volver
          </Button>
        </Group>
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="red"
          variant="light"
          title="DeCA no disponible"
          mx="md"
        >
          No hemos podido recuperar este DeCA.
        </Alert>
      </Stack>
    );
  }

  const envio = deca.envio;
  const qrUrl = deca.qrUrl ?? `${API_BASE}/public/deca/${deca.codigoPublico}`;
  const envioEstado = envio?.estado;
  const puedeModificar = envioEstado !== undefined && ESTADOS_ENVIO_MODIFICABLES.has(envioEstado);
  const codigoVisible = deca.codigoAnual ?? deca.codigoPublico;

  // El "estado" del DeCA se deriva del estado del envio (ver
  // `mapEnvioEstadoToDeCaEstado`). Por eso las acciones de ciclo de vida
  // (En curso / Finalizar / Cancelar) atacan al envio, no al DeCA
  // directamente — el backend valida la transicion legal y la cache del
  // DeCA se invalida automaticamente desde `useUpdateEnvioEstado`.
  const handleSetEstado = (estado: EnvioEstado) => {
    if (!envio) return;
    cambiarEstado.mutate(
      { id: envio.id, estado },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Estado actualizado',
            message: `El envío ${envio.id.slice(0, 8)} ha pasado a ${estado}.`,
            color: estado === 'CANCELADO' ? 'red' : 'green',
            icon: <IconCheck size={18} />,
          });
          setCancelOpen(false);
        },
        onError: (e: unknown) =>
          notifications.show({
            title: 'Error al cambiar el estado',
            message: getErrorMessage(e),
            color: 'red',
            icon: <IconAlertCircle size={18} />,
          }),
      },
    );
  };

  return (
    <Stack gap="md" maw={1100} mx="auto" px="md" pt="md" pb="xl">
      {/* Cabecera */}
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="sm">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={18} />}
            onClick={() => navigate('/deca')}
          >
            Volver
          </Button>
          <Title order={2} fw={600}>
            DeCA ·{' '}
            <Text component="span" ff="monospace" inherit>
              {codigoVisible}
            </Text>
          </Title>
          <Badge size="lg" color="deca" variant="light">
            {deca.estado}
          </Badge>
        </Group>
        <Group gap="sm" wrap="nowrap">
          {/* Acciones de ciclo de vida del DeCA (cambian el estado del envio
              asociado, que es lo que persiste — el estado del DeCA se deriva). */}
          {deca.estado === 'VIGENTE' && (
            <Tooltip label="Marcar el envío EN_CURSO (servicio en marcha)">
              <Button
                color="deca"
                leftSection={<IconPlayerPlay size={16} />}
                onClick={() => handleSetEstado('EN_CURSO')}
                loading={cambiarEstado.isPending}
              >
                Iniciar servicio
              </Button>
            </Tooltip>
          )}
          {deca.estado === 'EN_CURSO' && (
            <Tooltip label="Marcar el envío FINALIZADO (servicio terminado OK)">
              <Button
                color="deca"
                leftSection={<IconFlag size={16} />}
                onClick={() => handleSetEstado('FINALIZADO')}
                loading={cambiarEstado.isPending}
              >
                Finalizar servicio
              </Button>
            </Tooltip>
          )}
          {(deca.estado === 'VIGENTE' || deca.estado === 'EN_CURSO') && (
            <Tooltip label="Anular el DeCA y cancelar el envío asociado">
              <Button
                color="red"
                variant="light"
                leftSection={<IconBan size={16} />}
                onClick={() => setCancelOpen(true)}
                disabled={cambiarEstado.isPending}
              >
                Cancelar
              </Button>
            </Tooltip>
          )}
          <Button
            variant="default"
            leftSection={<IconEdit size={16} />}
            onClick={() => navigate(`/deca/${deca.id}/editar`)}
            disabled={!puedeModificar}
            title={
              puedeModificar
                ? 'Editar DeCA (Método A) — abre el wizard completo'
                : 'Solo se puede modificar durante la realización del servicio (envío EMITIDO o EN_CURSO)'
            }
          >
            Modificar DeCA
          </Button>
        </Group>
      </Group>

      {!puedeModificar && envioEstado && (
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="yellow"
          variant="light"
          title={`No se puede modificar (envío ${envioEstado})`}
        >
          La modificación solo está permitida mientras el envío está en estado EMITIDO o EN_CURSO.
          Apartado Quinto de la Resolución 5 junio 2026.
        </Alert>
      )}

      {/* Cabecera del DeCA: codigos, estado, fechas, version */}
      <SectionCard
        title="Cabecera del DeCA"
        icon={IconReceipt}
        subtitle="Identificadores legales del documento y version vigente."
      >
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FieldRow icon={IconHash} label="Código anual">
              <Text ff="monospace" fw={600}>
                {deca.codigoAnual ?? '—'}
              </Text>
              <Text size="xs" c="dimmed" ff="monospace">
                público {deca.codigoPublico}
              </Text>
            </FieldRow>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FieldRow icon={IconShieldCheck} label="Estado legal">
              <Badge
                size="md"
                color={deca.estado === 'VIGENTE' || deca.estado === 'EN_CURSO' ? 'deca' : 'gray'}
                variant="light"
              >
                {deca.estado}
              </Badge>
            </FieldRow>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FieldRow icon={IconCalendar} label="Fecha de emisión">
              {new Date(deca.fechaEmision).toLocaleString('es-ES')}
            </FieldRow>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FieldRow icon={IconFileText} label="Versión vigente">
              v{deca.versiones.length}
              <Text component="span" size="xs" c="dimmed">
                {' '}
                (QR/PDF regenerados en cada modificación)
              </Text>
            </FieldRow>
          </Grid.Col>
        </Grid>
      </SectionCard>

      {/* Datos del envio (cabecera) */}
      <SectionCard
        title="Envío"
        icon={IconClipboardList}
        subtitle="Cabecera del viaje asociado al DeCA."
      >
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FieldRow icon={IconRoute} label="Tipo de servicio">
              {envio ? (TIPO_SERVICIO_LABEL[envio.tipoServicio] ?? envio.tipoServicio) : '—'}
            </FieldRow>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FieldRow icon={IconShieldCheck} label="Estado del envío">
              <Badge variant="light" color="gray">
                {envio?.estado ?? '—'}
              </Badge>
            </FieldRow>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <FieldRow icon={IconCalendar} label="Fecha de servicio">
              {envio?.fechaServicio
                ? new Date(envio.fechaServicio).toLocaleDateString('es-ES')
                : '—'}
            </FieldRow>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <FieldRow icon={IconCalendar} label="Fecha fin de servicio">
              {envio?.fechaFinServicio
                ? new Date(envio.fechaFinServicio).toLocaleDateString('es-ES')
                : '—'}
            </FieldRow>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <FieldRow icon={IconCalendar} label="Hora salida prevista">
              {envio?.horaSalidaPrevista ?? '—'}
            </FieldRow>
          </Grid.Col>
          {envio?.observaciones && (
            <Grid.Col span={12}>
              <FieldRow icon={IconNotes} label="Observaciones">
                {envio.observaciones}
              </FieldRow>
            </Grid.Col>
          )}
          {envio?.detalleViajero && (
            <Grid.Col span={12}>
              <Divider label="Detalle del viajero" labelPosition="left" />
              <Grid mt="xs">
                <Grid.Col span={{ base: 12, md: 3 }}>
                  <FieldRow label="Modalidad">{envio.detalleViajero.modalidad}</FieldRow>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 5 }}>
                  <FieldRow label="Contratante">{envio.detalleViajero.contratanteNombre}</FieldRow>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 2 }}>
                  <FieldRow label="NIF contratante">
                    {envio.detalleViajero.contratanteNif ?? '—'}
                  </FieldRow>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 2 }}>
                  <FieldRow label="Nº viajeros">{envio.detalleViajero.numeroViajeros}</FieldRow>
                </Grid.Col>
              </Grid>
            </Grid.Col>
          )}
        </Grid>
      </SectionCard>

      {/* Partes */}
      <SectionCard
        title="Partes"
        icon={IconBuilding}
        subtitle="Cargador contractual, transportista efectivo, conductor y vehículo."
      >
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <EmpresaMiniView
            titulo="Cargador contractual"
            icono={IconBuilding}
            empresa={deca.cargador}
          />
          <EmpresaMiniView
            titulo="Transportista efectivo"
            icono={IconTruck}
            empresa={deca.transportista}
          />
          <ConductorMiniView conductor={deca.conductor} />
          <VehiculoMiniView vehiculo={deca.vehiculo} />
        </SimpleGrid>
      </SectionCard>

      {/* Servicios (origen/destino + mercancía) */}
      <SectionCard
        title={`Servicios (${deca.servicios?.length ?? 0})`}
        icon={IconRoute}
        subtitle="Operaciones de transporte que componen el DeCA. Un DeCA puede tener 1..N servicios."
      >
        {deca.servicios && deca.servicios.length > 0 ? (
          <Stack gap="md">
            {deca.servicios.map((s, idx) => (
              <ServicioCard key={s.id ?? idx} index={idx + 1} servicio={s} />
            ))}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            No hay servicios registrados para este DeCA.
          </Text>
        )}
      </SectionCard>

      {/* URL pública y QR */}
      <SectionCard
        title="Documento y QR público"
        icon={IconQrcode}
        subtitle="URL que el inspector escanea para abrir el PDF sin autenticación."
      >
        <Stack gap="sm">
          <FieldRow icon={IconQrcode} label="URL pública" alignTop>
            <Code block style={{ width: '100%' }}>
              {qrUrl}
            </Code>
          </FieldRow>
          <Group>
            <Button
              variant="default"
              leftSection={<IconCopy size={14} />}
              onClick={() => {
                navigator.clipboard.writeText(qrUrl);
                notifications.show({ message: 'URL copiada al portapapeles', color: 'green' });
              }}
            >
              Copiar URL
            </Button>
            <Button
              variant="light"
              color="deca"
              leftSection={<IconDownload size={14} />}
              onClick={() => window.open(qrUrl, '_blank')}
            >
              Descargar PDF
            </Button>
          </Group>
        </Stack>
      </SectionCard>

      {/* Historico de modificaciones */}
      <SectionCard
        title="Histórico de modificaciones"
        icon={IconHistory}
        subtitle="Cada cambio durante la realización del servicio genera una nueva versión con motivo obligatorio."
      >
        {isLoadingVersiones ? (
          <Center mih={80}>
            <Loader size="sm" />
          </Center>
        ) : versiones && versiones.length > 0 ? (
          <Table withTableBorder withColumnBorders striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>v</Table.Th>
                <Table.Th>Fecha</Table.Th>
                <Table.Th>Método</Table.Th>
                <Table.Th>Motivo</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {versiones.map((v) => (
                <Table.Tr
                  key={v.version}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setVersionSeleccionada(v.version)}
                  data-testid={`version-row-${v.version}`}
                >
                  <Table.Td>
                    <Badge color={v.version === versiones.length ? 'deca' : 'gray'} variant="light">
                      v{v.version}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {(v.fechaModificacion ?? v.fechaCreacion).slice(0, 16).replace('T', ' ')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {v.metodoModificacion ?? 'inicial'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{v.motivoModificacion ?? '—'}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <Text size="sm" c="dimmed">
            No hay modificaciones registradas.
          </Text>
        )}
        <Text size="xs" c="dimmed">
          La versión {versiones?.length ?? 1} es la vigente. Las anteriores quedan registradas para
          trazabilidad.
        </Text>
      </SectionCard>

      {/* Auditoria */}
      <SectionCard
        title="Auditoría"
        icon={IconInfoCircle}
        subtitle="Metadatos de creación y última modificación."
      >
        <Grid>
          {deca.createdBy && (
            <Grid.Col span={{ base: 12, md: 6 }}>
              <FieldRow label="Creado por">{deca.createdBy}</FieldRow>
            </Grid.Col>
          )}
          {deca.createdAt && (
            <Grid.Col span={{ base: 12, md: 6 }}>
              <FieldRow label="Fecha de creación">
                {new Date(deca.createdAt).toLocaleString('es-ES')}
              </FieldRow>
            </Grid.Col>
          )}
          {deca.updatedAt && (
            <Grid.Col span={{ base: 12, md: 6 }}>
              <FieldRow label="Última actualización">
                {new Date(deca.updatedAt).toLocaleString('es-ES')}
              </FieldRow>
            </Grid.Col>
          )}
        </Grid>
      </SectionCard>

      <Text size="xs" c="dimmed" ta="center">
        <Anchor href="/api/docs" target="_blank" c="dimmed">
          Ver API docs (Swagger)
        </Anchor>
      </Text>

      {/* Drill-down de version */}
      <VersionDrillDownModal
        decaId={deca.id}
        version={versionSeleccionada}
        onClose={() => setVersionSeleccionada(null)}
      />

      {/* Confirmacion de cancelacion (Sprint X: desde DeCA, no desde envio). */}
      <ConfirmDialog
        opened={cancelOpen}
        title="Cancelar DeCA"
        message={
          envio
            ? `¿Cancelar este DeCA? El envío ${envio.id.slice(0, 8)}… pasará a estado CANCELADO y el documento quedará anulado. Esta acción no se puede deshacer.`
            : '¿Cancelar este DeCA? Esta acción no se puede deshacer.'
        }
        confirmLabel="Cancelar DeCA"
        confirmColor="red"
        loading={cambiarEstado.isPending}
        onConfirm={() => handleSetEstado('CANCELADO')}
        onCancel={() => setCancelOpen(false)}
      />
    </Stack>
  );
}

// ────────────────────────────────────────────────────────────────────
// Subcomponentes de layout uniforme
// ────────────────────────────────────────────────────────────────────

interface SectionCardProps {
  title: string;
  subtitle?: string;
  icon: typeof IconReceipt;
  children: React.ReactNode;
}

/**
 * Seccion con titulo + icono + subtitulo + contenido. Layout uniforme
 * para todas las secciones del detalle. Padding consistente,
 * separadores visibles entre campos.
 */
function SectionCard({ title, subtitle, icon: IconCmp, children }: SectionCardProps) {
  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="md">
        <Group gap="xs" align="center">
          <IconCmp size={20} color="var(--mantine-color-deca-7)" />
          <Title order={4} fw={600}>
            {title}
          </Title>
        </Group>
        {subtitle && (
          <Text size="sm" c="dimmed">
            {subtitle}
          </Text>
        )}
        <Divider />
        {children}
      </Stack>
    </Card>
  );
}

interface FieldRowProps {
  icon?: typeof IconReceipt;
  label: string;
  alignTop?: boolean;
  children: React.ReactNode;
}

/**
 * Linea label + valor con layout uniforme. El label va a la izquierda
 * en color dimmed; el valor a la derecha. Si pasas un icono, aparece
 * delante del label. Si `alignTop` es true, el contenido se alinea
 * arriba (util cuando el valor ocupa varias lineas).
 */
function FieldRow({ icon: IconCmp, label, alignTop, children }: FieldRowProps) {
  return (
    <Group justify="space-between" align={alignTop ? 'flex-start' : 'center'} wrap="nowrap">
      <Group gap={6} wrap="nowrap" style={{ minWidth: 0, flex: '0 0 auto' }}>
        {IconCmp && <IconCmp size={14} color="var(--mantine-color-gray-6)" />}
        <Text size="sm" c="dimmed">
          {label}
        </Text>
      </Group>
      <Stack gap={2} align="flex-end" style={{ minWidth: 0, flex: 1 }}>
        {children}
      </Stack>
    </Group>
  );
}

interface EmpresaMiniViewProps {
  titulo: string;
  icono: typeof IconBuilding;
  empresa: DeCaEnriched['cargador'];
}
function EmpresaMiniView({ titulo, icono: IconCmp, empresa }: EmpresaMiniViewProps) {
  return (
    <Card withBorder padding="sm" radius="sm">
      <Stack gap={6}>
        <Group gap={6}>
          <IconCmp size={14} color="var(--mantine-color-deca-7)" />
          <Text size="sm" fw={600}>
            {titulo}
          </Text>
        </Group>
        {empresa ? (
          <>
            <Text size="sm" fw={500}>
              {empresa.razonSocial}
            </Text>
            {empresa.nombreComercial && (
              <Text size="xs" c="dimmed">
                {empresa.nombreComercial}
              </Text>
            )}
            <Group gap={4}>
              <Text size="xs" c="dimmed">
                NIF/CIF:
              </Text>
              <Text size="xs" ff="monospace">
                {empresa.nifCif}
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {empresa.domicilioFiscal}
            </Text>
            {(empresa.municipio || empresa.provincia) && (
              <Text size="xs" c="dimmed">
                {[empresa.codigoPostal, empresa.municipio, empresa.provincia]
                  .filter(Boolean)
                  .join(', ')}
              </Text>
            )}
            {empresa.telefono && (
              <Text size="xs" c="dimmed">
                Tel: {empresa.telefono}
              </Text>
            )}
            {empresa.email && (
              <Text size="xs" c="dimmed">
                {empresa.email}
              </Text>
            )}
            <Badge
              size="xs"
              color={empresa.activo ? 'deca' : 'gray'}
              variant="light"
              w="fit-content"
            >
              {empresa.activo ? 'Activa' : 'Inactiva'}
            </Badge>
          </>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function ConductorMiniView({ conductor }: { conductor: DeCaEnriched['conductor'] }) {
  return (
    <Card withBorder padding="sm" radius="sm">
      <Stack gap={6}>
        <Group gap={6}>
          <IconUser size={14} color="var(--mantine-color-deca-7)" />
          <Text size="sm" fw={600}>
            Conductor
          </Text>
        </Group>
        {conductor ? (
          <>
            <Text size="sm" fw={500}>
              {conductor.nombre}
            </Text>
            {conductor.dni && (
              <Group gap={4}>
                <Text size="xs" c="dimmed">
                  DNI/NIE:
                </Text>
                <Text size="xs" ff="monospace">
                  {conductor.dni}
                </Text>
              </Group>
            )}
            {conductor.telefono && (
              <Text size="xs" c="dimmed">
                Tel: {conductor.telefono}
              </Text>
            )}
            {conductor.email && (
              <Text size="xs" c="dimmed">
                {conductor.email}
              </Text>
            )}
            {conductor.direccion && (
              <Text size="xs" c="dimmed">
                {conductor.direccion}
              </Text>
            )}
            <Badge
              size="xs"
              color={conductor.activo ? 'deca' : 'gray'}
              variant="light"
              w="fit-content"
            >
              {conductor.activo ? 'Activo' : 'Inactivo'}
            </Badge>
          </>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function VehiculoMiniView({ vehiculo }: { vehiculo: DeCaEnriched['vehiculo'] }) {
  return (
    <Card withBorder padding="sm" radius="sm">
      <Stack gap={6}>
        <Group gap={6}>
          <IconTruck size={14} color="var(--mantine-color-deca-7)" />
          <Text size="sm" fw={600}>
            Vehículo
          </Text>
        </Group>
        {vehiculo ? (
          <>
            <Text size="sm" fw={500} ff="monospace">
              {vehiculo.matricula}
            </Text>
            {(vehiculo.marca || vehiculo.modelo) && (
              <Text size="xs" c="dimmed">
                {[vehiculo.marca, vehiculo.modelo].filter(Boolean).join(' ')}
              </Text>
            )}
            {vehiculo.matriculaRemolque && (
              <Group gap={4}>
                <Text size="xs" c="dimmed">
                  Remolque:
                </Text>
                <Text size="xs" ff="monospace">
                  {vehiculo.matriculaRemolque}
                </Text>
              </Group>
            )}
            {typeof vehiculo.tara === 'number' && (
              <Text size="xs" c="dimmed">
                Tara: {vehiculo.tara} kg
              </Text>
            )}
            {typeof vehiculo.mma === 'number' && (
              <Text size="xs" c="dimmed">
                MMA: {vehiculo.mma} kg
              </Text>
            )}
            <Badge
              size="xs"
              color={vehiculo.activo ? 'deca' : 'gray'}
              variant="light"
              w="fit-content"
            >
              {vehiculo.activo ? 'Activo' : 'Inactivo'}
            </Badge>
          </>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        )}
      </Stack>
    </Card>
  );
}

interface ServicioCardProps {
  index: number;
  servicio: NonNullable<DeCaEnriched['servicios']>[number];
}
function ServicioCard({ index, servicio }: ServicioCardProps) {
  return (
    <Card withBorder padding="sm" radius="sm" bg="gray.0">
      <Stack gap="xs">
        <Group justify="space-between">
          <Text size="sm" fw={600}>
            Servicio {index}
          </Text>
          {servicio.id && (
            <Text size="xs" c="dimmed" ff="monospace">
              {servicio.id}
            </Text>
          )}
        </Group>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap={4}>
              <Group gap={4}>
                <IconRoute size={12} color="var(--mantine-color-gray-7)" />
                <Text size="xs" c="dimmed" fw={500}>
                  Origen
                </Text>
              </Group>
              <Text size="sm" fw={500}>
                {servicio.origen?.nombre ?? '—'}
              </Text>
              {servicio.origen?.direccion && (
                <Text size="xs" c="dimmed">
                  {servicio.origen.direccion}
                </Text>
              )}
              {(servicio.origen?.codigoPostal ||
                servicio.origen?.poblacion ||
                servicio.origen?.provincia) && (
                <Text size="xs" c="dimmed">
                  {[
                    servicio.origen.codigoPostal,
                    servicio.origen.poblacion,
                    servicio.origen.provincia,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Text>
              )}
              {servicio.origen?.nifCif && (
                <Group gap={4}>
                  <Text size="xs" c="dimmed">
                    NIF:
                  </Text>
                  <Text size="xs" ff="monospace">
                    {servicio.origen.nifCif}
                  </Text>
                </Group>
              )}
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap={4}>
              <Group gap={4}>
                <IconRoute size={12} color="var(--mantine-color-gray-7)" />
                <Text size="xs" c="dimmed" fw={500}>
                  Destino
                </Text>
              </Group>
              <Text size="sm" fw={500}>
                {servicio.destino?.nombre ?? '—'}
              </Text>
              {servicio.destino?.direccion && (
                <Text size="xs" c="dimmed">
                  {servicio.destino.direccion}
                </Text>
              )}
              {(servicio.destino?.codigoPostal ||
                servicio.destino?.poblacion ||
                servicio.destino?.provincia) && (
                <Text size="xs" c="dimmed">
                  {[
                    servicio.destino.codigoPostal,
                    servicio.destino.poblacion,
                    servicio.destino.provincia,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Text>
              )}
              {servicio.destino?.nifCif && (
                <Group gap={4}>
                  <Text size="xs" c="dimmed">
                    NIF:
                  </Text>
                  <Text size="xs" ff="monospace">
                    {servicio.destino.nifCif}
                  </Text>
                </Group>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
        <Divider />
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xs">
          <FieldLine icon={IconBox} label="Naturaleza" value={servicio.naturaleza} />
          <FieldLine
            icon={IconWeight}
            label="Cantidad"
            value={
              typeof servicio.cantidad === 'number' && servicio.unidadCantidad
                ? `${servicio.cantidad} ${UNIDAD_CANTIDAD_LABEL[servicio.unidadCantidad]}`
                : '—'
            }
          />
          <FieldLine
            icon={IconHash}
            label="Bultos"
            value={servicio.bultos !== undefined ? String(servicio.bultos) : '—'}
          />
        </SimpleGrid>
        {servicio.observaciones && (
          <FieldLine icon={IconNotes} label="Observaciones" value={servicio.observaciones} />
        )}
      </Stack>
    </Card>
  );
}

/**
 * Linea label + valor compacta, sin icono izquierdo ni alineacion
 * vertical. Usada dentro de los SimpleGrid de ServicioCard.
 */
function FieldLine({
  icon: IconCmp,
  label,
  value,
}: {
  icon?: typeof IconReceipt;
  label: string;
  value: string;
}) {
  return (
    <Stack gap={2}>
      <Group gap={4}>
        {IconCmp && <IconCmp size={12} color="var(--mantine-color-gray-6)" />}
        <Text size="xs" c="dimmed" fw={500}>
          {label}
        </Text>
      </Group>
      <Text size="sm">{value || '—'}</Text>
    </Stack>
  );
}

// ────────────────────────────────────────────────────────────────────
// Drill-down de una version concreta (Sprint 5.3)
// ────────────────────────────────────────────────────────────────────

function VersionDrillDownModal({
  decaId,
  version,
  onClose,
}: {
  decaId: string;
  version: number | null;
  onClose: () => void;
}) {
  const { data: v, isLoading, error } = useVersionDeCA(decaId, version ?? undefined);

  return (
    <Modal
      opened={version !== null}
      onClose={onClose}
      title={version !== null ? `Detalle de la versión v${version}` : 'Detalle de versión'}
      size="lg"
    >
      {version === null ? null : isLoading ? (
        <Center mih={120}>
          <Loader size="sm" />
        </Center>
      ) : error || !v ? (
        <Alert icon={<IconAlertCircle size={18} />} color="red" variant="light">
          No hemos podido recuperar esta versión.
        </Alert>
      ) : (
        <VersionView version={v} />
      )}
    </Modal>
  );
}

function VersionView({ version: v }: { version: DeCaVersion }) {
  return (
    <Stack gap="sm">
      <Group gap="xs" align="center">
        <Badge size="lg" color="deca" variant="light">
          v{v.version}
        </Badge>
        <Text size="sm" c="dimmed">
          {v.metodoModificacion ? `Método ${v.metodoModificacion}` : 'Versión inicial'}
        </Text>
      </Group>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <FieldRow icon={IconCalendar} label="Fecha">
            {(v.fechaModificacion ?? v.fechaCreacion).slice(0, 16).replace('T', ' ')}
          </FieldRow>
        </Grid.Col>
        {v.codigoPublico && (
          <Grid.Col span={{ base: 12, md: 6 }}>
            <FieldRow icon={IconQrcode} label="QR de esta versión">
              <Text size="xs" ff="monospace">
                {v.codigoPublico}
              </Text>
            </FieldRow>
          </Grid.Col>
        )}
      </Grid>
      {v.motivoModificacion && (
        <FieldRow icon={IconNotes} label="Motivo" alignTop>
          <Text size="sm" ta="right" maw={400}>
            {v.motivoModificacion}
          </Text>
        </FieldRow>
      )}
      <Divider label="Envío en esta versión" labelPosition="left" />
      {v.envioSnapshot ? (
        <EnvioSnapshotView envio={v.envioSnapshot as Record<string, unknown>} />
      ) : (
        <Text size="sm" c="dimmed">
          Esta versión no incluye snapshot del envío.
        </Text>
      )}
    </Stack>
  );
}

/**
 * Vista del envioSnapshot: mostramos los mismos campos que el envio
 * actual pero con los datos exactos al momento de la version.
 * Estilo "raw" (no enriquecido): los IDs son IDs, los resolver ya
 * seria enrichment y aqui no aporta valor.
 */
function EnvioSnapshotView({ envio }: { envio: Record<string, unknown> }) {
  const field = (label: string, value: unknown) => (
    <FieldRow label={label}>
      {value === undefined || value === null || value === '' ? '—' : String(value)}
    </FieldRow>
  );
  const detalle = envio.detalleMercancia as
    { naturaleza?: string; pesoKg?: number; bultos?: number } | undefined;
  return (
    <Card withBorder padding="sm" radius="sm">
      <Stack gap="xs">
        {field('Tipo servicio', envio.tipoServicio)}
        {field('Estado', envio.estado)}
        {field('Fecha servicio', envio.fechaServicio)}
        {field('Fecha fin servicio', envio.fechaFinServicio)}
        {field('Hora salida prevista', envio.horaSalidaPrevista)}
        <Divider />
        {field(
          'Origen',
          `${envio.origenLocalidad ?? '—'}${envio.origenProvincia ? ` (${envio.origenProvincia})` : ''}`,
        )}
        {field(
          'Destino',
          `${envio.destinoLocalidad ?? '—'}${envio.destinoProvincia ? ` (${envio.destinoProvincia})` : ''}`,
        )}
        {detalle && (
          <>
            <Divider label="Mercancía" labelPosition="left" />
            {field('Naturaleza', detalle.naturaleza)}
            {field('Peso (kg)', detalle.pesoKg)}
            {field('Bultos', detalle.bultos)}
          </>
        )}
      </Stack>
    </Card>
  );
}
